import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import '../Team/people.css';
import './TeamManagement.css';
import EmployeePopup from '../Profile/EmployeePopup';
import TeamTimeOff from '../Team/TeamTimeOff';
import TeamTraining from '../Team/TeamTraining';
import TeamCompliance from '../Team/TeamCompliance';
import TeamOnboarding from '../Team/TeamOnboarding';
import api, { normalizeArray } from '../../apiClient';
import { Table, THead, TRow, TCell } from '../ui/table';

/* Team — managing people. The directory + THE employee card (EmployeePopup,
   the only person surface) + management views across people (Time off queue,
   Training, Compliance checklist, Onboarding). Every row in those views opens
   the same card on the matching tab. Running the business (fleet, inventory,
   dispatch, technicians as a schedule resource) is Operations, not here. */
const TEAM_TABS = [
  { key: 'directory', label: 'Directory', path: '/team' },
  { key: 'time-off', label: 'Time off', path: '/team/time-off' },
  { key: 'training', label: 'Training', path: '/team/training' },
  { key: 'compliance', label: 'Compliance', path: '/team/compliance' },
  { key: 'onboarding', label: 'Onboarding', path: '/team/onboarding' },
];

const TEAM_LIST_ENDPOINTS = ['/accounts/team/', '/accounts/users/', '/tenant/users/'];
const TEAM_CREATE_ENDPOINTS = [
  '/accounts/team/add-member/',
  '/accounts/add-team-member/',
  '/accounts/users/',
  '/tenant/users/',
];
const ROUTING_ENDPOINT = '/accounts/voice/routing/';

const ROLE_OPTIONS = [
  { value: 'Tech', label: 'Member (Tech)' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Admin', label: 'Admin' },
];

const AFTER_HOURS_OPTIONS = [
  { value: 'VOICEMAIL', label: 'Voicemail' },
  { value: 'AI', label: 'AI Assistant' },
  { value: 'RING_GROUP', label: 'Ring Group' },
];

const DEFAULT_ROUTING_DRAFT = {
  name: 'Default',
  ai_first: false,
  ai_override_enabled: false,
  business_hours_start: '',
  business_hours_end: '',
  timezone: 'America/New_York',
  after_hours_route: 'VOICEMAIL',
  default_ring_timeout: 15,
  members: [],
};

const isEndpointFallbackStatus = (status) => status === 404 || status === 405;

const parseApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.members)) return payload.members;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  if (Array.isArray(payload?.data?.users)) return payload.data.users;
  return normalizeArray(payload);
};

const normalizeRole = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'admin') return 'Admin';
  if (normalized === 'manager') return 'Manager';
  if (normalized === 'tech' || normalized === 'member' || normalized === 'technician') return 'Tech';
  return '';
};

const resolveRoleFromGroups = (groups = []) => {
  if (!Array.isArray(groups)) return '';
  for (const item of groups) {
    const roleValue = typeof item === 'string' ? item : item?.name || item?.title || item?.role;
    const normalized = normalizeRole(roleValue);
    if (normalized) return normalized;
  }
  return '';
};

const resolveMemberRole = (member = {}) => {
  const directCandidates = [
    member?.role,
    member?.role_name,
    member?.role_type,
    member?.group,
    member?.group_name,
    member?.user_type,
  ];

  for (const candidate of directCandidates) {
    const normalized = normalizeRole(candidate);
    if (normalized) return normalized;
  }

  return resolveRoleFromGroups(member?.groups || member?.group_names || member?.user_groups) || 'Tech';
};

const normalizeMember = (member = {}) => ({
  ...member,
  id: Number(member?.id || member?.user_id || member?.pk || 0),
  first_name: member?.first_name || member?.firstName || '',
  last_name: member?.last_name || member?.lastName || '',
  email: member?.email || '',
  role: resolveMemberRole(member),
});

const readApiError = (error, fallbackMessage) =>
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  fallbackMessage;

const normalizeTimeInputValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.length >= 5 ? raw.slice(0, 5) : raw;
};

const toPositiveInt = (value, fallback = 15) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 1) return fallback;
  return Math.round(parsed);
};

const reorderMembers = (members, sourceUserId, targetUserId) => {
  const sourceIndex = members.findIndex((item) => Number(item.user_id) === Number(sourceUserId));
  const targetIndex = members.findIndex((item) => Number(item.user_id) === Number(targetUserId));

  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
    return members;
  }

  const next = [...members];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
};

const mapRoutingResponseToDraft = (payload = {}) => {
  const policy = payload?.policy || {};
  const firstGroup = Array.isArray(payload?.ring_groups) && payload.ring_groups.length > 0
    ? payload.ring_groups[0]
    : null;
  const rawMembers = Array.isArray(firstGroup?.members) ? firstGroup.members : [];

  const members = [...rawMembers]
    .sort((a, b) => Number(a?.order_index || 0) - Number(b?.order_index || 0))
    .map((member, index) => ({
      user_id: Number(member?.user_id || 0),
      order_index: Number(member?.order_index ?? index),
      ring_timeout: toPositiveInt(member?.ring_timeout, toPositiveInt(policy?.default_ring_timeout, 15)),
      is_active: member?.is_active !== false,
    }))
    .filter((member) => member.user_id > 0);

  return {
    name: String(policy?.name || DEFAULT_ROUTING_DRAFT.name),
    ai_first: !!policy?.ai_first,
    ai_override_enabled: !!policy?.ai_override_enabled,
    business_hours_start: normalizeTimeInputValue(policy?.business_hours_start),
    business_hours_end: normalizeTimeInputValue(policy?.business_hours_end),
    timezone: String(policy?.timezone || DEFAULT_ROUTING_DRAFT.timezone),
    after_hours_route: String(policy?.after_hours_route || DEFAULT_ROUTING_DRAFT.after_hours_route),
    default_ring_timeout: toPositiveInt(policy?.default_ring_timeout, 15),
    members,
  };
};

const ROLE_LABEL = { Tech: 'Technician', Manager: 'Manager', Admin: 'Admin' };
// The person cell of the directory table (avatar + name); the row itself opens them.
const UserRow = ({ member }) => {
  const initials = `${(member.first_name || '')[0] || ''}${(member.last_name || '')[0] || ''}`.toUpperCase() || (member.email || '?')[0].toUpperCase();
  return (
    <span className="user-row">
      {member.avatar ? <img src={member.avatar} alt="" className="user-row-avatar" /> : <span className="user-row-avatar user-row-initials">{initials}</span>}
      <span className="user-row-name">{[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email}</span>
    </span>
  );
};

const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    role: 'Tech',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [popupTab, setPopupTab] = useState('info');
  const location = useLocation();
  const teamTab = useMemo(() => {
    const seg = location.pathname.replace(/^\/team\/?/, '').split('/')[0];
    return TEAM_TABS.some((t) => t.key === seg) ? seg : 'directory';
  }, [location.pathname]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Tech');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);

  const [routingDraft, setRoutingDraft] = useState(DEFAULT_ROUTING_DRAFT);
  const [routingLoading, setRoutingLoading] = useState(true);
  const [routingSaving, setRoutingSaving] = useState(false);
  const [routingError, setRoutingError] = useState(null);
  const [routingSuccess, setRoutingSuccess] = useState(null);
  const [candidateUserId, setCandidateUserId] = useState('');
  const [draggedUserId, setDraggedUserId] = useState(null);


  const memberById = useMemo(
    () =>
      normalizeArray(teamMembers).reduce((acc, member) => {
        if (Number(member?.id) > 0) acc.set(Number(member.id), member);
        return acc;
      }, new Map()),
    [teamMembers]
  );

  const selectedRingUserIds = useMemo(
    () => new Set((routingDraft?.members || []).map((member) => Number(member.user_id))),
    [routingDraft]
  );

  const availableRingCandidates = useMemo(
    () => normalizeArray(teamMembers).filter((member) => !selectedRingUserIds.has(Number(member.id))),
    [teamMembers, selectedRingUserIds]
  );

  const fetchTeamMembers = useCallback(async () => {
    setFetching(true);
    setError(null);

    try {
      let lastError = null;

      for (const endpoint of TEAM_LIST_ENDPOINTS) {
        try {
          const response = await api.get(endpoint);
          const members = parseApiList(response.data)
            .map((member) => normalizeMember(member))
            .filter((member) => Number(member.id) > 0);
          setTeamMembers(members);
          return;
        } catch (err) {
          lastError = err;
          if (isEndpointFallbackStatus(err?.response?.status)) continue;
          break;
        }
      }

      setTeamMembers([]);
      setError(readApiError(lastError, 'Unable to load team members. Please try again.'));
    } catch (err) {
      setTeamMembers([]);
      setError(readApiError(err, 'Unable to load team members. Please try again.'));
    } finally {
      setFetching(false);
    }
  }, []);

  const fetchRoutingPolicy = useCallback(async () => {
    setRoutingLoading(true);
    setRoutingError(null);

    try {
      const response = await api.get(ROUTING_ENDPOINT);
      setRoutingDraft(mapRoutingResponseToDraft(response.data));
    } catch (err) {
      setRoutingError(readApiError(err, 'Unable to load routing policy.'));
    } finally {
      setRoutingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
    fetchRoutingPolicy();
  }, [fetchTeamMembers, fetchRoutingPolicy]);

  useEffect(() => {
    setRoutingDraft((previous) => {
      const validUserIds = new Set(normalizeArray(teamMembers).map((member) => Number(member.id)));
      const filtered = (previous.members || []).filter((member) => validUserIds.has(Number(member.user_id)));
      if (filtered.length === (previous.members || []).length) {
        return previous;
      }
      return {
        ...previous,
        members: filtered.map((member, index) => ({ ...member, order_index: index })),
      };
    });
  }, [teamMembers]);

  useEffect(() => {
    if (!availableRingCandidates.length) {
      setCandidateUserId('');
      return;
    }

    const selected = Number(candidateUserId);
    if (!selected || !availableRingCandidates.some((candidate) => Number(candidate.id) === selected)) {
      setCandidateUserId(String(availableRingCandidates[0].id));
    }
  }, [availableRingCandidates, candidateUserId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setNewMember((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddMember = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const payload = {
      first_name: newMember.firstName,
      last_name: newMember.lastName,
      email: newMember.email,
      phone_number: newMember.phoneNumber,
      password: newMember.password,
      role: newMember.role,
      is_active: true,
    };

    try {
      let response = null;
      let lastError = null;

      for (const endpoint of TEAM_CREATE_ENDPOINTS) {
        try {
          response = await api.post(endpoint, payload);
          break;
        } catch (err) {
          lastError = err;
          if (isEndpointFallbackStatus(err?.response?.status)) continue;
          break;
        }
      }

      if (!response) {
        throw lastError || new Error('Unable to create team member.');
      }

      setSuccess(response.data.message || 'User added successfully.');
      setNewMember({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        role: 'Tech',
      });
      await fetchTeamMembers();
      await fetchRoutingPolicy();
    } catch (err) {
      const backendError =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.response?.data?.phone_number?.[0] ||
        err.response?.data?.email?.[0] ||
        err.response?.data?.role?.[0] ||
        'Failed to add member. Please try again.';
      setError(backendError);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (event) => {
    event.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setError(null);
    setInviteSuccess(null);
    try {
      const { data } = await api.post('/accounts/auth/invite-user/', {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
      });
      setInviteSuccess(data?.detail || `Invitation sent to ${inviteEmail}.`);
      setInviteEmail('');
    } catch (err) {
      setError(readApiError(err, 'Failed to send invite.'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRoutingDraftChange = (patch) => {
    setRoutingSuccess(null);
    setRoutingDraft((previous) => ({ ...previous, ...(patch || {}) }));
  };

  const handleRingMemberChange = (userId, patch) => {
    setRoutingSuccess(null);
    setRoutingDraft((previous) => ({
      ...previous,
      members: (previous.members || []).map((member) =>
        Number(member.user_id) === Number(userId)
          ? { ...member, ...(patch || {}) }
          : member
      ),
    }));
  };

  const handleAddRingMember = () => {
    const nextUserId = Number(candidateUserId);
    if (!nextUserId) return;

    setRoutingSuccess(null);
    setRoutingDraft((previous) => {
      if ((previous.members || []).some((member) => Number(member.user_id) === nextUserId)) {
        return previous;
      }

      const defaultTimeout = toPositiveInt(previous.default_ring_timeout, 15);
      return {
        ...previous,
        members: [
          ...(previous.members || []),
          {
            user_id: nextUserId,
            order_index: (previous.members || []).length,
            ring_timeout: defaultTimeout,
            is_active: true,
          },
        ],
      };
    });
  };

  const handleRemoveRingMember = (userId) => {
    setRoutingSuccess(null);
    setRoutingDraft((previous) => ({
      ...previous,
      members: (previous.members || [])
        .filter((member) => Number(member.user_id) !== Number(userId))
        .map((member, index) => ({ ...member, order_index: index })),
    }));
  };

  const handleDragStart = (event, userId) => {
    setDraggedUserId(Number(userId));
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(userId));
  };

  const handleDropOnMember = (event, targetUserId) => {
    event.preventDefault();
    const sourceUserId = Number(event.dataTransfer.getData('text/plain') || draggedUserId);
    if (!sourceUserId || Number(targetUserId) === sourceUserId) {
      setDraggedUserId(null);
      return;
    }

    setRoutingSuccess(null);
    setRoutingDraft((previous) => ({
      ...previous,
      members: reorderMembers(previous.members || [], sourceUserId, Number(targetUserId)).map(
        (member, index) => ({ ...member, order_index: index })
      ),
    }));
    setDraggedUserId(null);
  };

  const handleSaveRouting = async () => {
    setRoutingSaving(true);
    setRoutingError(null);
    setRoutingSuccess(null);

    const defaultTimeout = toPositiveInt(routingDraft.default_ring_timeout, 15);
    const normalizedMembers = (routingDraft.members || []).map((member, index) => ({
      user_id: Number(member.user_id),
      order_index: index,
      ring_timeout: toPositiveInt(member.ring_timeout, defaultTimeout),
      is_active: member.is_active !== false,
    }));

    const payload = {
      name: (routingDraft.name || 'Default').trim() || 'Default',
      is_active: true,
      business_hours_start: routingDraft.business_hours_start || null,
      business_hours_end: routingDraft.business_hours_end || null,
      timezone: (routingDraft.timezone || 'America/New_York').trim() || 'America/New_York',
      after_hours_route: routingDraft.after_hours_route || 'VOICEMAIL',
      ai_override_enabled: !!routingDraft.ai_override_enabled,
      ai_first: !!routingDraft.ai_first,
      default_ring_timeout: defaultTimeout,
      ring_groups: [
        {
          name: 'Primary',
          order_index: 0,
          members: normalizedMembers,
        },
      ],
    };

    try {
      const response = await api.put(ROUTING_ENDPOINT, payload);
      setRoutingDraft(mapRoutingResponseToDraft(response.data));
      setRoutingSuccess('Routing policy saved.');
    } catch (err) {
      setRoutingError(readApiError(err, 'Unable to save routing policy.'));
    } finally {
      setRoutingSaving(false);
    }
  };

  const handleViewEmployee = (member, tab = 'info') => {
    setPopupTab(tab);
    setSelectedEmployee(member);
  };
  // Team views hand back a USER id; the card takes the directory member.
  const openEmployee = useCallback((userId, tab = 'info') => {
    const member = memberById.get(Number(userId));
    if (member) { setPopupTab(tab); setSelectedEmployee(member); }
    else setError('That person is not in the directory yet.');
  }, [memberById]);
  // ?open=<user id>&tab=<tab> deep link (Operations → SOPs coverage, emails, old /operations/roster/:id redirects)
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const uid = Number(q.get('open'));
    if (uid && memberById.size && !selectedEmployee) {
      const m = memberById.get(uid);
      if (m) { setPopupTab(q.get('tab') || 'info'); setSelectedEmployee(m); }
    }
  }, [location.search, memberById]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClosePopup = () => {
    setSelectedEmployee(null);
  };

  const handleSaveEmployee = async () => {
    await fetchTeamMembers();
    await fetchRoutingPolicy();
  };

  const members = normalizeArray(teamMembers);
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="team-page">
      {/* The section tabs live in the shell's top bar (Directory / Time off / …), and
          Log out is in the user menu — this page head is the title only. */}
      <div className="team-page-head">
        <div className="team-page-head-left">
          <h1>Team</h1>
        </div>
      </div>

      {success && <p className="success-message">{success}</p>}
      {error && <p className="error-message">{error}</p>}

      {teamTab === 'time-off' && <TeamTimeOff openEmployee={openEmployee} />}
      {teamTab === 'training' && <TeamTraining openEmployee={openEmployee} />}
      {teamTab === 'compliance' && <TeamCompliance openEmployee={openEmployee} />}
      {teamTab === 'onboarding' && <TeamOnboarding openEmployee={openEmployee} />}

      {teamTab === 'directory' && (<div className="team-tab">
      <section className="ppl-card ppl-card--wide">
        <div className="ppl-row-head">
          <h3>Directory{members.length ? <span className="ppl-count">{members.length}</span> : null}</h3>
          <div className="ppl-actions">
            <button type="button" className="ops-btn secondary" onClick={fetchTeamMembers} disabled={fetching}>Refresh</button>
            <button type="button" className="ops-btn" onClick={() => setAddOpen((o) => !o)}>{addOpen ? 'Close' : 'Add a person'}</button>
          </div>
        </div>

        {addOpen && (
          <div className="team-add">
            <form onSubmit={handleInviteMember} className="team-add-col">
              <div className="team-add-title">Invite by email</div>
              <p className="team-muted">They get a link and set up their own account. No password to hand over.</p>
              {inviteSuccess && <p className="success-message">{inviteSuccess}</p>}
              <div className="team-form">
                <input type="email" placeholder="Email address" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required />
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <button type="submit" className="ops-btn" disabled={inviteLoading}>{inviteLoading ? 'Sending…' : 'Send invite'}</button>
              </div>
            </form>
            <form onSubmit={handleAddMember} className="team-add-col">
              <div className="team-add-title">Create the account now</div>
              <p className="team-muted">For someone standing next to you. Set a temporary password; they change it on first login.</p>
              <div className="team-form">
                <input type="text" name="firstName" placeholder="First name" value={newMember.firstName} onChange={handleChange} required />
                <input type="text" name="lastName" placeholder="Last name" value={newMember.lastName} onChange={handleChange} required />
                <input type="email" name="email" placeholder="Email" value={newMember.email} onChange={handleChange} required />
                <input type="text" name="phoneNumber" placeholder="Phone" value={newMember.phoneNumber} onChange={handleChange} />
                <input type="password" name="password" placeholder="Temporary password" value={newMember.password} onChange={handleChange} required autoComplete="new-password" />
                <select name="role" value={newMember.role} onChange={handleChange} required>
                  {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <button type="submit" className="ops-btn" disabled={loading}>{loading ? 'Adding…' : 'Add member'}</button>
              </div>
            </form>
          </div>
        )}

        {fetching ? (
          <p className="team-muted">Loading…</p>
        ) : members.length === 0 ? (
          <div className="ppl-empty"><strong>You are the only member.</strong><span>Add a person to build the directory.</span></div>
        ) : (
          <Table className="team-table">
            <THead columns={[{ label: 'Name' }, { label: 'Email' }, { label: 'Phone' }, { label: 'Role' }]} />
            <tbody>
              {members.map((member) => (
                <TRow key={member.id || member.email} onClick={() => handleViewEmployee(member)}>
                  <TCell><UserRow member={member} /></TCell>
                  <TCell className="team-table__email">{member.email || '—'}</TCell>
                  <TCell className="team-table__phone">{member.phone_number || member.phone || '—'}</TCell>
                  <TCell><span className={`ppl-pill role-${String(member.role).toLowerCase()}`}>{ROLE_LABEL[member.role] || member.role}</span></TCell>
                </TRow>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section className="ppl-card ppl-card--wide team-routing-panel" aria-labelledby="team-routing-title">
        <div className="ppl-row-head">
          <h3 id="team-routing-title">Inbound ring routing</h3>
          <button type="button" className="ops-btn secondary" onClick={fetchRoutingPolicy} disabled={routingLoading || routingSaving}>Reload</button>
        </div>

        {routingError && <p className="error-message">{routingError}</p>}
        {routingSuccess && <p className="success-message">{routingSuccess}</p>}

        {routingLoading ? (
          <p className="team-muted">Loading routing policy...</p>
        ) : (
          <>
            <div className="team-routing-grid">
              <label>
                Policy Name
                <input
                  type="text"
                  value={routingDraft.name}
                  onChange={(event) => handleRoutingDraftChange({ name: event.target.value })}
                />
              </label>

              <label>
                Timezone
                <input
                  type="text"
                  value={routingDraft.timezone}
                  onChange={(event) => handleRoutingDraftChange({ timezone: event.target.value })}
                />
              </label>

              <label>
                Business Hours Start
                <input
                  type="time"
                  value={routingDraft.business_hours_start}
                  onChange={(event) =>
                    handleRoutingDraftChange({ business_hours_start: event.target.value })
                  }
                />
              </label>

              <label>
                Business Hours End
                <input
                  type="time"
                  value={routingDraft.business_hours_end}
                  onChange={(event) =>
                    handleRoutingDraftChange({ business_hours_end: event.target.value })
                  }
                />
              </label>

              <label>
                After Hours Route
                <select
                  value={routingDraft.after_hours_route}
                  onChange={(event) =>
                    handleRoutingDraftChange({ after_hours_route: event.target.value })
                  }
                >
                  {AFTER_HOURS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label>
                Default Ring Timeout (sec)
                <input
                  type="number"
                  min={1}
                  value={routingDraft.default_ring_timeout}
                  onChange={(event) =>
                    handleRoutingDraftChange({
                      default_ring_timeout: toPositiveInt(event.target.value, 15),
                    })
                  }
                />
              </label>
            </div>

            <div className="team-routing-toggles">
              <label className="team-toggle-item">
                <input
                  type="checkbox"
                  checked={!!routingDraft.ai_first}
                  onChange={(event) =>
                    handleRoutingDraftChange({ ai_first: event.target.checked })
                  }
                />
                AI-first mode
              </label>
              <label className="team-toggle-item">
                <input
                  type="checkbox"
                  checked={!!routingDraft.ai_override_enabled}
                  onChange={(event) =>
                    handleRoutingDraftChange({ ai_override_enabled: event.target.checked })
                  }
                />
                AI override enabled
              </label>
            </div>

            <div className="ring-group-editor">
              <div className="ring-group-editor-head">
                <h4>Primary Ring Group</h4>
                <p className="team-muted">
                  Drag to reorder users. Routing resolves as <code>user:{'{id}'}</code> in this order.
                </p>
              </div>

              <div className="ring-group-add-row">
                <select
                  value={candidateUserId}
                  onChange={(event) => setCandidateUserId(event.target.value)}
                  disabled={!availableRingCandidates.length}
                >
                  {availableRingCandidates.length ? (
                    availableRingCandidates.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.first_name} {candidate.last_name} ({candidate.email})
                      </option>
                    ))
                  ) : (
                    <option value="">No available team members</option>
                  )}
                </select>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={handleAddRingMember}
                  disabled={!availableRingCandidates.length}
                >
                  Add to Ring Group
                </button>
              </div>

              {(routingDraft.members || []).length === 0 ? (
                <p className="team-muted">No ring members configured.</p>
              ) : (
                <ul className="ring-member-list">
                  {(routingDraft.members || []).map((member) => {
                    const user = memberById.get(Number(member.user_id));
                    const displayName = user
                      ? `${user.first_name} ${user.last_name}`.trim()
                      : `User ${member.user_id}`;
                    const email = user?.email || '';

                    return (
                      <li
                        key={member.user_id}
                        className="ring-member-item"
                        draggable
                        onDragStart={(event) => handleDragStart(event, member.user_id)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => handleDropOnMember(event, member.user_id)}
                      >
                        <span className="ring-drag-handle" aria-hidden="true">::</span>
                        <div className="ring-member-meta">
                          <strong>{displayName}</strong>
                          <small>{email || `user:${member.user_id}`}</small>
                          <small className="ring-identity">Identity: user:{member.user_id}</small>
                        </div>

                        <label className="ring-timeout-field">
                          Timeout
                          <input
                            type="number"
                            min={1}
                            value={member.ring_timeout}
                            onChange={(event) =>
                              handleRingMemberChange(member.user_id, {
                                ring_timeout: toPositiveInt(event.target.value, routingDraft.default_ring_timeout),
                              })
                            }
                          />
                        </label>

                        <label className="ring-active-field">
                          <input
                            type="checkbox"
                            checked={member.is_active !== false}
                            onChange={(event) =>
                              handleRingMemberChange(member.user_id, {
                                is_active: event.target.checked,
                              })
                            }
                          />
                          Active
                        </label>

                        <button
                          type="button"
                          className="ring-remove-btn"
                          onClick={() => handleRemoveRingMember(member.user_id)}
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="team-routing-actions">
              <button type="button" className="ops-btn" onClick={handleSaveRouting} disabled={routingSaving}>
                {routingSaving ? 'Saving…' : 'Save routing policy'}
              </button>
            </div>
          </>
        )}
      </section>
      </div>)}

      {selectedEmployee && (
        <EmployeePopup
          key={`${selectedEmployee.id}-${popupTab}`}
          employee={selectedEmployee}
          initialTab={popupTab}
          onClose={handleClosePopup}
          onSave={handleSaveEmployee}
        />
      )}
    </div>
  );
};

export default TeamManagement;
